import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Typography,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers";

interface AddCommunicationNoteProps {
  open: boolean;
  onClose: () => void;
  onSave: (note: {
    id?: string;
    communicationDate: string;
    method: string;
    summary: string;
    outcome: string;
    followUpDate: string;
  }) => void;
  note?: {
    id?: string;
    communicationDate: string;
    method: string;
    summary: string;
    outcome: string;
    followUpDate: string;
  } | null;
}

const AddCommunicationNote: React.FC<AddCommunicationNoteProps> = ({
  open,
  onClose,
  onSave,
  note,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [communicationDate, setCommunicationDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [method, setMethod] = useState("");
  const [summary, setSummary] = useState("");
  const [outcome, setOutcome] = useState("");
  const [followUpDate, setFollowUpDate] = useState<Date | null>(null);

  const [errors, setErrors] = useState({
    communicationDate: false,
    method: false,
    summary: false,
    outcome: false,
    followUpDate: false,
  });

  useEffect(() => {
    if (note) {
      setCommunicationDate(
        note.communicationDate
          ? new Date(note.communicationDate).toISOString().slice(0, 16)
          : ""
      );
      setMethod(note.method || "");
      setSummary(note.summary || "");
      setOutcome(note.outcome || "");
      setFollowUpDate(note.followUpDate ? new Date(note.followUpDate) : null);
    } else {
      setCommunicationDate(new Date().toISOString().slice(0, 16));
      setMethod("");
      setSummary("");
      setOutcome("");
      setFollowUpDate(null);
    }
  }, [note, open]);

  const handleSave = () => {
    const newErrors = {
      communicationDate: !communicationDate,
      method: !method,
      summary: !summary,
      outcome: !outcome,
      followUpDate: !followUpDate,
    };
    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((val) => val);
    if (hasError) return;

    const date = followUpDate!;
    onSave({
      id: note?.id,
      communicationDate,
      method,
      summary,
      outcome,
      followUpDate: new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString(),
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          maxHeight: { xs: '100vh', sm: '90vh' },
          width: { xs: '100vw', sm: 'auto' },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
          px: { xs: 2, sm: 3 },
          pb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          component="span"
          className="font-bold text-gray-800"
        >
          {note ? "Edit Communication Note" : "Add Communication Note"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          mt: 2,
          px: { xs: 2, sm: 3 },
          pb: 1
        }}
        className="custom-scrollbar"
      >
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Date / Time"
                value={communicationDate ? new Date(communicationDate) : null}
                onChange={(date) => {
                  if (date) {
                    setCommunicationDate(date.toISOString().slice(0, 16));
                  } else {
                    setCommunicationDate("");
                  }
                }}
                maxDateTime={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(errors.communicationDate),
                    helperText: errors.communicationDate
                      ? "Date/Time is required"
                      : "",
                    InputProps: {
                      sx: {
                        borderRadius: "10px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: "10px",
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label="Method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                fullWidth
                required
                variant="outlined"
                error={errors.method}
                helperText={errors.method ? "Method is required" : ""}
                InputProps={{ style: { borderRadius: 10 } }}
              >
                <MenuItem value="Email">📧 Email</MenuItem>
                <MenuItem value="Phone Call">📞 Phone Call</MenuItem>
                <MenuItem value="Text Message">💬 Text Message</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {/* follow up date */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Follow Up Date *"
                  value={followUpDate}
                  onChange={(date) => {
                    setFollowUpDate(date);
                  }}
                  minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(errors.followUpDate),
                      helperText: errors.followUpDate
                        ? "Follow-up date is required"
                        : "",
                      placeholder: "MM-DD-YYYY",
                      InputLabelProps: { shrink: true },
                      InputProps: {
                        sx: {
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderRadius: "10px",
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                fullWidth
                multiline
                rows={3}
                required
                placeholder="Enter a brief summary of the communication..."
                variant="outlined"
                error={errors.summary}
                helperText={errors.summary ? "Summary is required" : ""}
                InputProps={{ style: { borderRadius: 10 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                fullWidth
                multiline
                rows={3}
                required
                placeholder="Enter the outcome of the conversation..."
                variant="outlined"
                error={errors.outcome}
                helperText={errors.outcome ? "Outcome is required" : ""}
                InputProps={{ style: { borderRadius: 10 } }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{
        px: { xs: 2, sm: 3 },
        pb: 2,
        pt: 1,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Button
          onClick={onClose}
          color="error"
          variant="outlined"
          startIcon={<CloseIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            width: { xs: '100%', sm: 'auto' },
            order: { xs: 2, sm: 1 }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            width: { xs: '100%', sm: 'auto' },
            order: { xs: 1, sm: 2 }
          }}
        >
          {note ? "Update Note" : "Save Note"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCommunicationNote;
